// Team logo utility - generates initials-based logos
export function getTeamInitials(teamName) {
  if (!teamName) return "?";
  
  // Common Swedish football name patterns
  const name = teamName
    .replace(/\s+(IF|FK|FF|BK|SK|IS|IK|AIF|AIK|BIF|GIF|GoIF|GOIF|BoIS)\s*$/i, "")
    .replace(/^(IF|FK|FF|BK|SK|IS|IK)\s+/i, "");
  
  // Split into words
  const words = name.trim().split(/\s+/);
  
  if (words.length === 1) {
    // Single word - take first 2-3 letters
    return words[0].substring(0, 3).toUpperCase();
  }
  
  // Multiple words - take first letter of each (max 3)
  return words
    .slice(0, 3)
    .map(w => w[0])
    .join("")
    .toUpperCase();
}

export function getTeamLogoColors(division) {
  const colorSchemes = {
    "Allsvenskan": { bg: "#FECC00", text: "#0F172A" },
    "Superettan": { bg: "#334155", text: "#FFFFFF" },
    "Division 1": { bg: "#3B82F6", text: "#FFFFFF" },
    "Division 2": { bg: "#22C55E", text: "#FFFFFF" },
    "Division 3": { bg: "#F97316", text: "#FFFFFF" },
    "Division 4": { bg: "#A855F7", text: "#FFFFFF" },
    "Division 5": { bg: "#EC4899", text: "#FFFFFF" },
    "Division 6": { bg: "#14B8A6", text: "#FFFFFF" },
    "Division 7": { bg: "#64748B", text: "#FFFFFF" },
  };
  return colorSchemes[division] || { bg: "#94A3B8", text: "#FFFFFF" };
}

// Team Logo Component
export function TeamLogo({ teamName, division, size = "md", className = "", logoUrl = "" }) {
  const initials = getTeamInitials(teamName);
  const colors = getTeamLogoColors(division);
  
  const sizeClasses = {
    xs: "w-6 h-6 text-[8px]",
    sm: "w-8 h-8 text-[10px]",
    md: "w-10 h-10 text-xs",
    lg: "w-14 h-14 text-sm",
    xl: "w-20 h-20 text-lg",
  };

  const imgSizes = { xs: 24, sm: 32, md: 40, lg: 56, xl: 80 };
  
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={teamName}
        width={imgSizes[size]}
        height={imgSizes[size]}
        className={`object-contain ${sizeClasses[size].split(" ").slice(0, 2).join(" ")} ${className}`}
        loading="lazy"
        onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
      />
    );
  }

  return (
    <div
      className={`rounded-full flex items-center justify-center font-bold ${sizeClasses[size]} ${className}`}
      style={{ backgroundColor: colors.bg, color: colors.text }}
      title={teamName}
    >
      {initials}
    </div>
  );
}

// Social Share utilities
export function getShareUrl() {
  return typeof window !== "undefined" ? window.location.href : "";
}

export function shareOnTwitter(text, url) {
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
  const newWindow = window.open(twitterUrl, "_blank", "noopener,noreferrer,width=550,height=420");
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    // Popup blocked - use direct navigation
    window.location.href = twitterUrl;
  }
}

export function shareOnFacebook(url) {
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const newWindow = window.open(fbUrl, "_blank", "noopener,noreferrer,width=550,height=420");
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    window.location.href = fbUrl;
  }
}

export function shareOnInstagram(text) {
  // Instagram doesn't have a web sharing API
  // Best we can do is copy the text and inform the user
  const copied = copyToClipboard(text);
  return copied;
}

export function shareOnTikTok(text) {
  // TikTok doesn't have a web sharing API either
  // Best we can do is copy the text and inform the user
  const copied = copyToClipboard(text);
  return copied;
}

export function shareOnWhatsApp(text, url) {
  const waUrl = `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`;
  const newWindow = window.open(waUrl, "_blank", "noopener,noreferrer");
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    window.location.href = waUrl;
  }
}

// Generate share text for different contexts
export function generateShareText(type, data = {}) {
  switch (type) {
    case "team":
      const teamHashtag = data.teamName ? data.teamName.replace(/\s+/g, "") : "";
      return {
        text: `${data.teamName} ligger pa plats #${data.rank} i Sveriges fotbollsranking med ${data.points} poang!\n\nSe hela statistiken pa Fotbollsliv.com`,
        hashtags: `#svenskfotboll #fotboll #${teamHashtag}`,
      };
    case "table":
      return {
        text: `Kolla in Super-maratontabellen for sasong ${data.season}!\n\n${data.totalTeams || 1000} lag fran Allsvenskan till Division 5 rankade i en gemensam tabell.\n\nSe hela tabellen pa Fotbollsliv.com`,
        hashtags: "#svenskfotboll #fotboll #Allsvenskan #Superettan",
      };
    case "comparison":
      return {
        text: `Jamforelse: ${data.team1} vs ${data.team2}\n\nVem ar bast? Kolla statistiken pa Fotbollsliv.com!`,
        hashtags: "#svenskfotboll #fotboll",
      };
    default:
      return {
        text: `Upptack Sveriges fotbollsranking pa Fotbollsliv.com!\n\nAlla lag fran Allsvenskan till Division 5 i en gemensam tabell.`,
        hashtags: "#svenskfotboll #fotboll",
      };
  }
}

export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // Clipboard API failed, try fallback
  }
  
  // Fallback for browsers without clipboard API
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "-9999px";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);
    return success;
  } catch (err) {
    console.error("Failed to copy text:", err);
    return false;
  }
}
